import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { filterAndSort } from 'src/app/search.utils';
import { LocationGroupService } from 'src/app/service/location-group.service';
import { LocationService } from 'src/app/service/location.service';
import { UserLoginService } from 'src/app/service/user-login.service';
import { PanModalComponent } from '../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../error.utils';
import { NotifyService } from '../../service/notify.service';
import { DomainRow, LocationDomainWhitelist, LocationResponse } from '../../types';

@Component({
  selector: 'app-admin-user-management-location-domains-whitelist',
  templateUrl: './admin-user-management-location-domains-whitelist.component.html',
  standalone: false,
})
export class AdminUserManagementLocationDomainsWhitelistComponent implements OnInit {
  private locationGroupService = inject(LocationGroupService);
  private locationService = inject(LocationService);
  private notifications = inject(NotifyService);
  public userLogin = inject(UserLoginService);

  public selectedLocation = computed(() => {
    const id = this.selectedLocationId();
    if (id == null) return undefined;
    return this.locations().find((l) => l.id === id);
  });
  public selectedLocationId = signal<number | undefined>(undefined);
  public hasLoadedLocations = signal<boolean>(false);
  public locations = signal<LocationResponse[]>([]);
  public locationFilter = signal<string>('');
  public domainWhitelist = computed<LocationDomainWhitelist | undefined>(() => {
    const location = this.selectedLocation();
    if (!location) return undefined;

    return {
      location_id: location.id,
      location_domains: location.location_domain_whitelists ?? [],
      inherited_domains: (location.location_group_whitelists ?? []).map((domain) => ({
        domain,
        location_group_id: location.location_group_id ?? '',
        location_group_name: location.location_group_name ?? '',
      })),
    };
  });
  public hasLoadedDomains = computed<boolean>(() => !!this.selectedLocation());
  public selectedLocationDomains = computed<DomainRow[]>(() => {
    const data = this.domainWhitelist();
    if (!data) return [];

    return [
      ...data.location_domains.map((domain) => ({ domain, inherited: false })),
      ...data.inherited_domains.map((i) => ({
        domain: i.domain,
        inherited: true,
        locationGroupId: i.location_group_id,
        locationGroupName: i.location_group_name,
      })),
    ];
  });
  public domainFilter = signal<string>('');
  public filteredDomains = computed(() => {
    const filter = this.domainFilter().toLowerCase();
    if (!filter) return this.selectedLocationDomains();
    return this.selectedLocationDomains().filter((row) => row.domain.toLowerCase().includes(filter));
  });

  public hasNoDomains = computed(() => this.hasLoadedDomains() && this.selectedLocationDomains().length === 0);
  public addDomainModal = viewChild<PanModalComponent>('addDomainModal');
  public visibleLocations = computed(() => filterAndSort(this.locations(), this.locationFilter()));
  public newDomain = signal<string>('');
  public canSubmitModal = computed(() => !!this.selectedLocation() && this.newDomain().trim().length > 0);

  ngOnInit(): void {
    this.loadLocations();
  }

  loadLocations(): void {
    this.hasLoadedLocations.set(false);
    this.locations.set([]);

    this.locationService.getAllLocations({ include_whitelist_domains: true }).subscribe(({ items }) => {
      this.locations.set(items);
      this.hasLoadedLocations.set(true);
    });
  }

  selectLocation(location: LocationResponse): void {
    this.selectedLocationId.set(location.id);
    this.domainFilter.set('');
  }

  removeDomain(domain: string): void {
    const location = this.selectedLocation();
    if (!location) return;

    this.locationService.removeWhitelistDomain(location.id, domain).subscribe({
      next: (data) => {
        this.updateLocationDomain(location.id, data);
        this.notifications.notify(`Successfully removed domain "${domain}" from location "${location.name}"`);
      },
      error: (resp) => this.notifications.notify(tryGetErrorMessage(resp)),
    });
  }

  removeInheritedDomain(row: DomainRow): void {
    if (!row.locationGroupId) return;

    const remainingDomains = (this.domainWhitelist()?.inherited_domains ?? []).filter((d) => d.domain !== row.domain).map((d) => d.domain);

    this.locationGroupService.updateLocationGroupWhitelist(row.locationGroupId, { domain_whitelist: remainingDomains }).subscribe(() => {
      this.notifications.notify(`Successfully removed domain "${row.domain}" from location group "${row.locationGroupName}"`);
      this.locations.update((locations) =>
        locations.map((l) => (l.location_group_id === row.locationGroupId ? { ...l, location_group_whitelists: remainingDomains } : l)),
      );
    });
  }

  submitModal(): void {
    const location = this.selectedLocation();
    if (!location) return;

    const domain = this.newDomain().trim();
    if (!domain) return;

    if (this.selectedLocationDomains().some((row) => row.domain === domain)) {
      this.notifications.notify(`Domain "${domain}" already exists in the whitelist`);
      return;
    }

    this.locationService.addWhitelistDomain(location.id, domain).subscribe({
      next: (data) => {
        this.updateLocationDomain(location.id, data);
        this.notifications.notify(`Successfully added domain "${domain}" to location "${location.name}"`);
        this.addDomainModal()?.hide();
      },
      error: (resp) => this.notifications.notify(tryGetErrorMessage(resp)),
      complete: () => this.newDomain.set(''),
    });
  }

  private updateLocationDomain(locationId: number, data: LocationDomainWhitelist): void {
    this.locations.update((locations) =>
      locations.map((l) =>
        l.id === locationId
          ? {
              ...l,
              location_domain_whitelists: data.location_domains,
              location_group_whitelists: data.inherited_domains.map((d) => d.domain),
            }
          : l,
      ),
    );
  }
}
